import { useRouter } from 'next/router';
import { useTracking as useReactTracking } from 'react-tracking';
import getPublicRuntimeConfig from 'utils/publicRuntimeConfig';
import {
  BackButton,
  Form,
  FormProvider,
  NavEventProps,
  NavProvider,
  ScreenFields,
  useNextRouterNavigator,
} from '@updater/ui-informant';
import { SkipLinkWithDisclaimer } from 'components';
import { wrapperComponents } from './informant';
import { buildQuizLayout } from './informant/schema/buildQuizLayout';
import { withSeededAnswers } from './informant/withSeededAnswers';
import { Content } from './Content';
import { NextButton } from './NextButton';
import { ScreenHeading } from './ScreenHeading';
import { SessionstoragePersistence } from '@updater/ui-utilities';
import { SortMethod, useFilterAndSortOffers } from '@updater/ui-widgets';
import { useCallback, useContext, useMemo, useRef } from 'react';
import { useAcceptUpdaterTerms, useIsIFramed } from 'hooks';
import { MicrositeContext } from 'context';
import { PageRoutes } from 'constants/pageRoutes';
import styled from '@emotion/styled';

const QuizContainer = styled.div<{ center: boolean }>`
  margin: ${(props) => (props.center ? '0 auto' : '0')};
`;

export function QuizWrapper() {
  const router = useRouter();
  const publicRuntimeConfig = getPublicRuntimeConfig();
  const micrositeConfig = useContext(MicrositeContext);
  const isIFramed = useIsIFramed();
  // Back pops history instead of pushing, so in-quiz and browser back stay in sync.
  const navigator = useNextRouterNavigator({
    basePath: publicRuntimeConfig.basePath,
    useBack: true,
  });
  const { trackEvent: reactTrackEvent } = useReactTracking();
  const acceptUpdaterTerms = useAcceptUpdaterTerms();
  // Which questions this microsite asks, and the answers supplied on the
  // visitor's behalf for the ones it skips.
  const {
    schema: activeSchema,
    layoutSchema: activeLayoutSchema,
    initialValues,
    restartPolicy,
  } = useMemo(
    () =>
      buildQuizLayout({
        hideFirstPageBackButton: isIFramed,
      }),
    [isIFramed]
  );
  const quizPersistence = useMemo(
    () =>
      withSeededAnswers(
        SessionstoragePersistence(micrositeConfig.micrositeCode),
        initialValues
      ),
    [micrositeConfig.micrositeCode, initialValues]
  );
  const { setProductByPackages } = useFilterAndSortOffers({
    defaultSortMethod: SortMethod.PriceLowToHigh,
    defaultSelections: {
      products: [],
      providers: [],
      additionalLanguages: [],
    },
  });

  /**
   * Accepted when the visitor moves themselves forward past a question, which
   * is precisely what the disclaimer beneath every screen says they agree to by
   * doing. Once per run through the quiz — though note `SkipLinkWithDisclaimer`
   * accepts on its own click through a separate hook instance this cannot see,
   * so the two paths together can still report twice.
   *
   * Deliberately a ref rather than the persisted `acceptedUpdaterTerms` flag.
   * That flag never expires, and consent is reported alongside the address it
   * was given for, so gating on it would leave a visitor returning months later
   * for a different address with no consent record for that lead.
   */
  const hasAcceptedTermsThisQuiz = useRef(false);

  const acceptUpdaterTermsOnce = useCallback(() => {
    if (hasAcceptedTermsThisQuiz.current) {
      return;
    }
    hasAcceptedTermsThisQuiz.current = true;
    acceptUpdaterTerms();
  }, [acceptUpdaterTerms]);

  const onSubmit = (values, context) => {
    // The last question has nowhere to navigate to, so finishing the quiz
    // arrives here rather than in onBeforeNavigate.
    acceptUpdaterTermsOnce();
    reactTrackEvent({
      object: 'quiz_complete',
      verb: 'submitted',
      details: values,
    });
    context.setSubmitting(false);
    router.push(PageRoutes.plans);
  };

  const onFirstScreenBack = () => {
    // eslint-disable-next-line no-restricted-globals
    history.back();
  };

  const onChange = useCallback(() => {
    if (!setProductByPackages) return;
    // The quiz asks about no services at all, so there is nothing to filter
    // plans by. Cleared rather than left alone, so a filter stored by an
    // earlier visit does not narrow the page invisibly.
    setProductByPackages({
      internet: false,
      tv: false,
      phone: false,
      mobile: false,
    });
  }, [setProductByPackages]);

  const onBeforeNavigate = useCallback(
    ({ fromScreen, fromIndex, toIndex }: NavEventProps) => {
      // https://updater.atlassian.net/jira/software/c/projects/TVI/boards/541?assignee=60bbd7523aa6050068a5fca0&selectedIssue=TVI-244
      // informant runs this from one navigate() in both directions, and also
      // for the redirect it performs on mount, so neither the back button nor a
      // deep link should be read as agreement. Only forward progress from a
      // screen the visitor was actually on counts.
      if (fromScreen && toIndex > fromIndex) {
        acceptUpdaterTermsOnce();
      }
    },
    [acceptUpdaterTermsOnce]
  );

  return (
    <FormProvider
      schema={activeSchema}
      layoutSchema={activeLayoutSchema}
      initialValues={initialValues}
      trackEvent={reactTrackEvent}
      onSubmit={onSubmit}
      onChange={onChange}
      wrapperComponents={wrapperComponents}
      persistence={{ connector: quizPersistence }}
    >
      <NavProvider
        navigator={navigator}
        onFirstScreenBack={onFirstScreenBack}
        onBeforeNavigate={onBeforeNavigate}
        restartPolicy={restartPolicy}
      >
        <QuizContainer
          center={micrositeConfig?.centerQuizContainer}
          data-testid="quiz-container"
        >
          <Form>
            <Content>
              <BackButton />
              <ScreenHeading />
              <ScreenFields />
              <NextButton />
            </Content>
          </Form>
          <SkipLinkWithDisclaimer />
        </QuizContainer>
      </NavProvider>
    </FormProvider>
  );
}
